/**
 * Manages focus trapping within specified HTML elements using a stack.
 *
 * API:
 * Focus.trap(htmlElement): Adds a focus trap for the given element.
 * Focus.release(): Removes the most recent focus trap.
 * Focus.flush(): Removes all active focus traps.
 */
export default class Focus {

  /** @type {{element: HTMLElement, previouslyFocused: HTMLElement | null}[]} */
  static #trapStack = [];

  static #FOCUSABLE_SELECTORS = [
    "summary",
    "a[href]",
    "button:enabled",
    "[tabindex]:not([tabindex^='-'])",
    "[draggable]",
    "area",
    "input:not([type=hidden]):enabled",
    "select:enabled",
    "textarea:enabled",
    "object",
    "iframe"
  ];


  /**
   * Traps focus within the given HTML element.
   *
   * @param {HTMLElement} element - The element to trap focus within.
   * @public
   * @static
   */
  static trap(element) {
    if (!element || !(element instanceof HTMLElement)) {
      console.error('Focus.trap requires a valid HTMLElement.', element);
      return;
    }

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (Focus.#trapStack.length === 0) Focus.#addListeners();

    Focus.#trapStack.push({
      element: element,
      previouslyFocused: previouslyFocused
    });

    setTimeout(() => {
      const focusable = Focus.#getFocusableElements(element);
      if (focusable.length === 0) console.warn('Focus.trap called on an element with no focusable children:', element);
      if (focusable.length > 0) focusable[0].focus();
    }, 50);
  }

  /**
   * Releases the most recently added focus trap.
   *
   * @param {HTMLElement} [element] - The specific element whose trap should be released.
   * @public
   * @static
   */
  static release(element) {
    if (element == null) {
      console.warn('Focus.release called without an element.');
      return;
    }
    if (Focus.#trapStack.length === 0) {
      console.warn('Focus.release called, but no active traps.');
      return;
    }

    const releaseTrapIndex = Focus.#trapStack.findIndex(t => t.element === element);

    if (releaseTrapIndex < 0) return;

    const releasedTrap = Focus.#trapStack[releaseTrapIndex];
    Focus.#trapStack.length = releaseTrapIndex;

    if (Focus.#trapStack.length === 0) Focus.#removeListeners();

    releasedTrap.previouslyFocused.focus();
  }

  /**
   * Releases all active focus traps.
   *
   * @public
   * @static
   */
  static flush() {
    const wasActive = Focus.#trapStack.length > 0;
    if (!wasActive) {
      console.warn('Focus.flush called, but no active traps.');
      return;
    }

    const originalFocused = Focus.#bottom.previouslyFocused;

    Focus.#trapStack.length = 0; // Clear the stack

    Focus.#removeListeners();

    originalFocused.focus();
  }


  // Private

  /**
   * Gets the current top element info from the trap stack.
   */
  static get #top() {
    if (Focus.#trapStack.length === 0) return null;
    return Focus.#trapStack[Focus.#trapStack.length - 1];
  }

  /**
   * Gets the bottom element info from the trap stack.
   */
  static get #bottom() {
    if (Focus.#trapStack.length === 0) return null;
    return Focus.#trapStack[0];
  }

  static #getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        Focus.#FOCUSABLE_SELECTORS.join(',')
      )
    ).filter(el => {
      return (
        el.offsetWidth > 0 && el.offsetHeight > 0 &&
        window.getComputedStyle(el).visibility !== 'hidden'
      );
    });;
  }

  static #handleKeyDown = (event) => {
    if (Focus.#trapStack.length === 0 || event.key !== 'Tab' || event.defaultPrevented) return;

    const focusableElements = Focus.#getFocusableElements(Focus.#top.element);

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    }
    else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }

  };

  static #handleFocusIn = (event) => {
    if (Focus.#trapStack.length === 0 || event.defaultPrevented) return;

    if (Focus.#top.element !== event.target && !Focus.#top.element.contains(event.target)) {
      const focusable = Focus.#getFocusableElements(Focus.#top.element);
      event.preventDefault();

      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        Focus.#top.element.focus();
      }
    }
  };

  static #addListeners() {
    document.addEventListener('keydown', Focus.#handleKeyDown);
    document.addEventListener('focusin', Focus.#handleFocusIn);
  }

  static #removeListeners() {
    document.removeEventListener('keydown', Focus.#handleKeyDown);
    document.removeEventListener('focusin', Focus.#handleFocusIn);
  }
}
